import * as React from 'react';

import * as UI from 'core/ui';
import * as CloseDocumentStatus from './CloseDocumentStatus';
import * as Invoice from './Invoice';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { DocumentType, BuyerCustomerDTO, PaymentOperationDTO, InvoiceParamsFromPosDTO, SaleDTO, Res, PaymentDTO, PaymentMethodDTO, Permission } from 'core/api';
import { Result, CustomError, LocalStorage } from 'core/infrastructure';
import classnames from 'classnames';

import { Col, Cols, ErrorBlock, Flex } from 'core/ui';
import { Math, Currency } from 'core/utils';
import getPaymentNotes from './getPaymentNotes';
import * as Images from 'pos/Images';
import { ExternalPaymentsService } from 'pos/services';
import { Command } from 'pos/common';
import { Message, Modal } from 'core/ui-next/modal';

type SetDocumentPaymentProps = {
	layout: LayoutType,
	printDocument?: () => Promise<void>
	getDocument: () => Invoice.Document,
	setCustomer: (customer: BuyerCustomerDTO) => Promise<void>,
	paymentOperation: PaymentOperationDTO,
	onCloseDocument: (params: InvoiceParamsFromPosDTO) => Promise<Result<SaleDTO, CustomError>>
} & Modal.Actions<SaleDTO>;


export type Props = SetDocumentPaymentProps;

export type State = CloseDocumentStatus.Status & {
	// Necesario para que las clases hijas puedan remontar los controles de pago y propina
	// si se han cambiado desde la clase padre (por ejemplo, al pulsar el botón de pasar a propina).
	// Debe actualizarse siempre se que se haya cambiado el pendingAmount, el tip o el amount del estado
	proposedAmountKey: number,

};

export function ErrorMessage(props: { errorMsg: string }) {
	return props.errorMsg ? <Cols>
		<Col className='has-error text-center m-b-n' width='*'>
			<p><ErrorBlock msg={props.errorMsg} /></p>
		</Col>
	</Cols> : null;
}

export function PaymentsList({
	payments,
	onRemove,
	selectedPaymentIndex,
	onSelect,
	onAddNote
}: {
	payments: PaymentDTO[],
	onRemove: (payment: PaymentDTO) => void,
	selectedPaymentIndex: number,
	onSelect?: (selectedIndex: number) => void,
	onAddNote?: (selectedIndex: number, selectedPayment: PaymentDTO) => Promise<void>
}) {
	let list = React.createRef<HTMLUListElement>();

	const items = payments.map((p, i) => {
		const isSelected = selectedPaymentIndex === i;
		const showOnAddNote = isSelected && p.AllowExtraInformation && onAddNote;

		const className = classnames({
			selected: isSelected
		});

		return <li key={i} className={className} onClick={() => onSelect && onSelect(i)}>
			<Cols>
				<Col width='*'>
					{showOnAddNote
						? <button className='btn btn-info btn-xs pull-right' style={{ position: 'relative' }} onClick={() => onAddNote!(i, p)} >{Res.Web.PAYMENT_CONTROL_INFO_BTN()}</button>
						: null}
					{p.MethodName}
				</Col>
				<Col width='11ex' className='text-right m-r-xs'>
					{UI.Format.asMoney(p.Amount)}
				</Col>
				<Col width='8ex' className='text-right'>
					{UI.Format.asMoney(p.TipAmount)}
				</Col>
				<Col width='35px' onClick={() => onRemove(p)} ><i className='m-l text-danger fa fa-window-close' style={{ cursor: 'pointer' }}></i></Col>
			</Cols>
			{p.ExtraInformation ?
				<Cols>
					<Col className='text-muted' width='*'>
						{p.ExtraInformation}
					</Col>
				</Cols> : null}
		</li>;
	});

	React.useEffect(() => {
		const items = list.current!.getElementsByTagName('li');
		// Compruebo a ver si tiene la función porque en el WebView de android no existe y falla
		if (items.length !== 0 && (list! as any).scrollTo)
			list.current!.scrollTo(0, items[items.length - 1].offsetTop - list.current!.getBoundingClientRect().top);
	});

	return <Flex.Vertical>
		<Flex.Item>
			<Cols className='list-header' >
				<Col width='*'>
					{Res.Web.CLOSE_TICKET_PAYMENT_METHOD_CLM()}
				</Col>
				<Col width='12ex' className='text-right m-r-xs'>
					{Res.Web.CLOSE_TICKET_PAID_CLM()}
				</Col>
				<Col width='8ex' className='text-right'>
					{Res.Web.CLOSE_TICKET_TIP_CLM()}
				</Col>
				<Col width='32px' />
			</Cols>
		</Flex.Item>
		<Flex.Item size='*'>
			<ul ref={list} className='list' style={{ overflowY: 'auto' }} >
				{items}
			</ul>
		</Flex.Item>
	</Flex.Vertical>;
}

const PRINT_GIFT_NAME = 'PRINTGIFT';

export default abstract class CloseDocumentScreen extends PosScreenClassComponent<Props, State> {

	protected txtIntroducedAmount = React.createRef<UI.PriceBox<State>>();
	protected txtIntroducedTip = React.createRef<UI.PriceBox<State>>();
	protected chkPrintGift = React.createRef<UI.CheckBox<State>>();

	private externalPayments: ExternalPaymentsService;
	private layout: Layout;

	constructor(props: Props) {
		super(props);

		this.layout = this.buildLayout(props.layout);
		const isForRefund = props.layout === 'CreateRefund' || props.layout === 'RefundInvoice';
		const ticketGlobalId = props.getDocument().ticketGlobalId;
		this.externalPayments = isForRefund ? ExternalPaymentsService.forRefund('Refund', ticketGlobalId) : ExternalPaymentsService.forPay('Pay', ticketGlobalId);
	}

	createInitialState(props: Props): Nullable<State> {
		const printGift = LocalStorage.getItem(PRINT_GIFT_NAME);
		return {
			ErrorMessage: '',
			PaidAmount: 0,
			ChangeAmount: 0,
			AmountToPay: Math.round(props.getDocument().pendingAmount, Currency.DECIMAL_PLACES),
			PendingAmount: Math.round(props.getDocument().pendingAmount, Currency.DECIMAL_PLACES),
			Payments: [],
			SelectedMethod: -1,
			proposedAmountKey: 0,
			PrintGift: printGift && printGift === 'Yes' ? true : false,
			ThereIsEnoughChange: true,
			CurrentPaidAmount: 0,
			CurrentTipAmount: 0
		};
	}

	protected getSize(): ScreenSize {
		return { width: 1000, height: 700 };
	}

	protected onPrintGiftChanged(newValue: boolean) {
		LocalStorage.setItem(PRINT_GIFT_NAME, newValue ? 'Yes' : 'No');
	}

	onComponentDidMount(): void {
		setTimeout(() => {
			this.txtIntroducedAmount.current!.activate();
		});
	}

	protected async requestDismiss(): Promise<void> {

		await this.execute({
			action: async () => {
				let canDismiss = false;
				await this.externalPayments.cancel(() => canDismiss = true);
				if (canDismiss)
					this.cancel();
			}
		});
	}

	onComponentWillUnmount(): void { }

	protected getScreenId(): string {
		return 'CLOSE_DOCUMENT';
	}

	protected getTitle(): string {
		return this.layout.title;
	}

	protected getImage(): string | SvgIcon {
		return this.layout.image;
	}


	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.layout.buttons;
	}

	// Permito redefinir las acciones tras añadir o eliminar pagos para ajustar focos, scrolls, etc.
	protected afterPaymentAdded = () => { };
	protected afterPaymentRemoved = () => { };


	private buildLayout = (type: LayoutType): Layout => {

		switch (type) {
			case 'CloseTicket':
				return {
					type: 'CloseTicket',
					title: Res.Web.CLOSE_TICKET(),
					buttons: [
						this.createCancelButton(),
						null,
						this.createSetCustomerButton(),
						this.createPrintTicketButton(),
						this.createInvoiceButton(DocumentType.StandardInvoice),
						this.createInvoiceButton(DocumentType.BasicInvoice)
					],
					image: Images.CloseTicket
				};
			case 'CloseSalesOrder':
				return {
					type: 'CloseSalesOrder',
					title: Res.Web.CLOSE_TICKET(),
					buttons: [
						this.createCancelButton(),
						null,
						null,
						null,
						this.createInvoiceButton(DocumentType.StandardInvoice),
						this.createInvoiceButton(DocumentType.BasicInvoice)
					],
					image: Images.CloseTicket
				};
			case 'CloseDeliveryNote':
				return {
					type: 'CloseTicket',
					title: Res.Web.CLOSE_TICKET(),
					buttons: [
						this.createCancelButton(),
						null,
						null,
						null,
						this.createInvoiceButton(DocumentType.StandardInvoice),
						this.createInvoiceButton(DocumentType.BasicInvoice)
					],
					image: Images.CloseTicket
				};
			case 'CreateRefund':
				return {
					type: 'CreateRefund',
					title: Res.Web.CLOSE_TICKET_REFUND(),
					buttons: [
						this.createCancelButton(),
						null,
						this.createSetCustomerButton(),
						null,
						this.createRefundButton(DocumentType.StandardInvoice),
						this.createRefundButton(DocumentType.BasicInvoice)
					],
					image: Images.RefundTicket
				};
			case 'RefundInvoice':
				return {
					type: 'RefundInvoice',
					title: Res.Web.CLOSE_TICKET_REFUND(),
					buttons: [
						this.createCancelButton(),
						null,
						null,
						null,
						this.createRefundButton(DocumentType.StandardInvoice),
						this.createRefundButton(DocumentType.BasicInvoice)
					],
					image: Images.RefundTicket
				};
			case 'ChangeInvoicePayment':
				return {
					type: 'ChangeInvoicePayment',
					title: Res.Web.CLOSE_TICKET_CHANGE_PAYMENT(),
					buttons: [
						this.createCancelButton(),
						null,
						null,
						null,
						null,
						this.createChangeInvoicePaymentButton()
					],
					image: Images.ChangeInvoicePayment
				};
			default: throw new Error('Layout para CloseDocumentScreen no soportado');
		}
	};


	private createPrintTicketButton = () => {
		return this.props.printDocument !== undefined
			? { title: Res.Web.CLOSE_TICKET_PRINT_BTN(), image: Images.PrintTicket, action: this.props.printDocument }
			: null;
	};

	private createCancelButton = () => this.cancelButton(async () => await this.requestDismiss());

	private createInvoiceButton = (type: DocumentType) => ({
		title: type === DocumentType.BasicInvoice ? Res.Web.CLOSE_TICKET_CLOSE_BTN() : Res.Web.CLOSE_TICKET_CREATE_INVOICE_BTN(),
		image: type === DocumentType.BasicInvoice ? Images.CloseTicket : Images.CreateInvoice,
		action: async () => await this.handleProcessPaymentResult(
			async (commitCashPayments) => await Invoice.create({
				setCustomer: this.props.setCustomer,
				getDocument: this.props.getDocument,
				invoiceType: type,
				commitCashPayments,
				printGift: this.getPrintTicketGift(),
				payments: this.state.Payments,
				closeDocument: this.props.onCloseDocument,
			}))
	});

	private createSetCustomerButton = () => ({
		title: Res.Web.CLOSE_TICKET_SET_CUSTOMER_BTN(),
		image: Images.SetCustomer,
		action: async () => {
			await Command.execute('setOrRemoveCustomer', {
				customer: this.props.getDocument().customer,
				setCustomer: async (customer: BuyerCustomerDTO) => this.props.setCustomer(customer),
				hideRemoveCustomerButton: false,
				requiredPermission: Permission.Ticket_SetBuyerCustomer
			});
			await this.refreshAmounts();
		}
	});

	private refreshAmounts = async () => {
		const amountToPay = Math.round(this.props.getDocument().pendingAmount, Currency.DECIMAL_PLACES);
		const isRefund = this.props.layout === 'CreateRefund' || this.props.layout === 'RefundInvoice';
		this.setState(await CloseDocumentStatus.refresh(this.state, amountToPay, isRefund));
	};

	private createRefundButton = (type: DocumentType) => ({
		title: type === DocumentType.BasicInvoice ? Res.Web.CLOSE_TICKET_REFUND_BTN() : Res.Web.CLOSE_TICKET_CREATE_REFUND_INVOICE_BTN(),
		image: type === DocumentType.BasicInvoice ? Images.RefundTicket : Images.CreateInvoice,
		action: async () => await this.handleProcessPaymentResult(
			async (commitCashPayments) => await Invoice.refund({
				getDocument: this.props.getDocument,
				setCustomer: this.props.setCustomer,
				invoiceType: type,
				commitCashPayments,
				payments: this.state.Payments,
				refund: this.props.onCloseDocument
			}))
	});

	private createChangeInvoicePaymentButton = () => ({
		title: Res.Web.CLOSE_TICKET_CHANGE_PAYMENT_BTN(),
		image: Images.ChangeInvoicePayment,
		action: async () => await this.handleProcessPaymentResult(
			async (commitCashPayments) => await Invoice.changePayment({
				getDocument: this.props.getDocument,
				commitCashPayments,
				payments: this.state.Payments,
				printGift: this.getPrintTicketGift(),
				changePayments: this.props.onCloseDocument
			}))
	});

	handleProcessPaymentResult = async (paymentAction: (commitCashPayments: () => Promise<Result>) => Promise<Invoice.ProcessPaymentResult>) => {

		// Si todavía no he pagado lo suficiente no dejo terminar el pago
		if (this.state.AmountToPay > this.state.PaidAmount) {
			await Message.show(Res.Msg.MSG_NOT_ENOUGH_PAID_AMOUNT());
			return;
		}

		const change = this.state.Payments.some(x => x.IsRefundVoucher) ? 0 : this.state.ChangeAmount;
		const result = await paymentAction(() => this.externalPayments.commitCashPayments(change));

		if (result.ok) {
			this.close(result.value);
			return;
		}

		if (result.error === 'preconditions-failed') {
			await this.refreshAmounts();
			return;
		}

		// Ha fallado la operación contra la máquina de control de efectivo, o alguna precondición ha fallado,
		// como que no tengo permisos, que el total ya no coincide, etc. No cierro la pantalla por si puede solucionar
		// el problema. En el caso de que no pueda que devuelva los pagos.
		if (result.error === 'commit-cash-payments-failed') {
			return;
		}

		// Ha fallado la impresión, pero el ticket se ha cerrado y aún así no tengo acceso al SaleDTO porque el
		// result que me envían viene con error. No puedo hacer otra cosa que salir.
		if (result.error === 'print-failed') {
			this.cancel();
			return;
		}

		// Ha fallado la creación de la factura deshago los pagos que tuviera hechos y salgo
		await this.externalPayments.undoAll();
		this.cancel();
	};

	getProposedAmount = (): Result<{ amount: number, tip: number }> => {

		const amountResult = this.txtIntroducedAmount.current!.getValue();
		const tipResult = this.txtIntroducedTip.current!.getValue();

		if (!amountResult.ok || !tipResult.ok)
			return Result.error();

		const [amount, tip] = [amountResult.value || 0, tipResult.value || 0];

		// Si no ha introducido nada es un pago por todo lo que queda pendiente
		if (amount === 0 && tip === 0)
			return Result.ok({ amount: this.state.PendingAmount, tip });

		return Result.ok({ amount, tip });
	};

	getPrintTicketGift = (): boolean => {

		if (this.chkPrintGift.current === null)
			return false;

		const result = this.chkPrintGift.current!.getValue()!;
		return result.ok && result.value!;
	};

	protected onSelectPayment = (paymentIndex: number) => {
		this.setState({ SelectedMethod: paymentIndex });
	};

	protected onAddNote = async (paymentIndex: number, paymentMethod: PaymentDTO) => {
		const noteResult = await getPaymentNotes(paymentMethod);
		if (!noteResult.ok)
			return;

		const payments = this.state.Payments;
		payments[paymentIndex].ExtraInformation = noteResult.value;
		this.setState({ Payments: payments });
	};

	protected onRemovePayment = async (payment: PaymentDTO) => {

		await this.execute({
			action: async () => {
				const isRefund = this.props.layout === 'CreateRefund' || this.props.layout === 'RefundInvoice';
				const newPaymentStatus = await CloseDocumentStatus.remove(this.externalPayments, this.state, payment, isRefund);
				this.setState({
					...newPaymentStatus,
					proposedAmountKey: this.state.proposedAmountKey + 1
				}, () => this.afterPaymentRemoved());
			}
		});
	};

	protected onPaymentMethodSelected = async (method: PaymentMethodDTO) => {

		await this.execute({
			action: async () => {
				const proposedAmountResult = this.getProposedAmount();
				if (proposedAmountResult.ok) {
					const isRefund = this.props.layout === 'CreateRefund' || this.props.layout === 'RefundInvoice';
					const newPaymentStatus = await CloseDocumentStatus.add(this.externalPayments, this.state, method, proposedAmountResult.value, isRefund);
					this.setState({
						...newPaymentStatus,
						proposedAmountKey: this.state.proposedAmountKey + 1
					}, () => this.afterPaymentAdded());
				}
			}
		});
	};

	protected onSetTip = () => {
		const proposedAmountResult = this.getProposedAmount();
		if (proposedAmountResult.ok)
			this.setState({
				...CloseDocumentStatus.completeTip(this.state, proposedAmountResult.value),
				proposedAmountKey: this.state.proposedAmountKey + 1
			});
	};

	protected getContent(): React.ReactNode {
		return <></>;
	}
}

export type Layout = {
	buttons: Nullable<ActionButtonDefinition>[],
	title: string,
	image: string | SvgIcon
	type: LayoutType
};

export type LayoutType = 'CloseTicket' | 'CloseSalesOrder' | 'CloseDeliveryNote' | 'CreateRefund' | 'RefundInvoice' | 'ChangeInvoicePayment';
